import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import Select from "react-select";
import { Button } from "antd";
import debounce from "lodash.debounce";
import ApiService from "../util/ApiService";
import { FaSearch } from "react-icons/fa";
import AuthService from "../login/AuthService";
import HospitalModal from "./HospitalModal";

const HospitalSearch = forwardRef(({
                                       onHospitalSelect,
                                       initialHospital = null, // Hospital inicial, opcional
                                       inicialEditable = false, // Hospital inicial, opcional
                                       buttonAdd = true,
                                       statusTitle = true,
                                       latitude = false,
                                       longitude = false,
                                       raio = false,
                                       all  = false,
                                       validateManager =false,
                                       cache = true,
                                       autoSelectFirst = false,
                                   }, ref) => {
    const [showHospitalModal, setShowHospitalModal] = useState(false);
    const [statusInit, setStatusInit] = useState(false);
    const [users] = useState(AuthService.getUserData());
    const [hospitalSelected, setHospitalSelected] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const user = AuthService.getUserData();
    // Configurar hospital inicial ao carregar
    useEffect(() => {
        if (initialHospital) {
            setHospitalSelected({
                value: initialHospital.id,
                label: initialHospital.name,
                city: initialHospital.city_name || initialHospital.city?.name,
                uf: initialHospital.uf || initialHospital.city?.uf,
            });
        }
    }, [initialHospital]);

    useEffect(() => {
        if (!statusInit) {
            // sempre tenta carregar do cache
            const storedHospitals = localStorage.getItem("hospitals");
            if (cache && storedHospitals) {
                setSuggestions(JSON.parse(storedHospitals));
            } else {
                fetchSuggestions(); // primeira busca
            }
            setStatusInit(true);
        }
    }, []);


    const fetchSuggestions = async (searchTerm = null) => {
        setLoading(true);

        const cacheKey = "hospitals";

        // Se for pra usar cache, tentar buscar do localStorage
        if (cache && !searchTerm) {
            const storedHospitals = localStorage.getItem(cacheKey);
            if (storedHospitals) {
                const arrayStore  = JSON.parse(storedHospitals);

                if(arrayStore.length > 0) {
                    setSuggestions(arrayStore);
                    setLoading(false);
                    return;
                }
            }
        }

        const param = {
            name: searchTerm,
            all: all,
        };

        if (user?.users_type_id === 1 && validateManager) {
            param.id_user = user?.id;
        }

        try {
            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}hospitals/search`,
                param,
                false
            );

            const { data } = ApiService.handleApiResponse(response);
            const fetchedSuggestions = Array.isArray(data) ? data : [];

            const formattedSuggestions = fetchedSuggestions.map((hospital) => ({
                value: hospital.id,
                label: hospital.name,
                city: hospital.city_name,
                hourly_rate: hospital.value,
                uf: hospital.uf,
            }));

            setSuggestions(formattedSuggestions);

            // Armazena no cache local se for a primeira busca (sem termo)
            if (cache && !searchTerm) {
                localStorage.setItem(cacheKey, JSON.stringify(formattedSuggestions));
            }

            if (autoSelectFirst && formattedSuggestions.length > 0) {
                handleHospitalSelect(formattedSuggestions[0]);
            }


        } catch (error) {
            ApiService.handleApiError(error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };


    const debouncedFetchSuggestions = useCallback(debounce(fetchSuggestions, 500), [users, latitude, longitude, raio]);

    const handleInputChange = (inputValue) => {
        setInputValue(inputValue);

        if (inputValue) {
            debouncedFetchSuggestions(inputValue);
        }
    };

    const handleHospitalSelect = (selectedOption) => {
        setHospitalSelected(selectedOption); // Atualizar seleção atual
        if (onHospitalSelect) {
            onHospitalSelect(selectedOption); // Notificar componente pai
        }
    };

    const handleOpenHospitalModal = () => {
        setShowHospitalModal(true);
    };

    const handleHospitalModalClose = () => {
        setShowHospitalModal(false);
    };

    const handleClearSelection = () => {
        setHospitalSelected(null);
        setInputValue("");
        if (onHospitalSelect) {
            onHospitalSelect(null);
        }
    };

    // Permitir que o componente pai limpe a seleção
    useImperativeHandle(ref, () => ({
        clearHospital() {
            handleClearSelection();
        },
        reloadHospitals() {
            fetchSuggestions(); // força recarregamento das sugestões
        }
    }));

    return (
        <div className="form-group" style={{ position: "relative" }}>
            {statusTitle && <label>Local</label>}
            <div className="hospital-input-group d-flex align-items-center">
                <Select
                    placeholder="Nome do local"
                    value={hospitalSelected} // Valor inicial e selecionado
                    onInputChange={handleInputChange}
                    onChange={handleHospitalSelect}
                    options={suggestions}
                    isDisabled={Boolean(initialHospital) && !inicialEditable}
                    isClearable
                    isLoading={loading}
                    filterOption={() => true}
                    noOptionsMessage={() => "Nenhum hospital encontrado"}
                    loadingMessage={() => "Carregando..."}
                    formatOptionLabel={(option) => (
                        <div>
                            <div>{option.label}</div>
                        </div>
                    )}
                    menuPortalTarget={document.body} // Renderizar o menu fora do contêiner
                    styles={{
                        container: (provided) => ({ ...provided, flex: 1 }),
                        control: (provided) => ({ ...provided, minHeight: "38px" }),
                        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }), // Define z-index alto
                        menu: (provided) => ({ ...provided, zIndex: 9999 }), // Ajusta o z-index no menu
                    }}

                />

                {buttonAdd && (
                    <button type="button"
                        className="btn btn-outline-info"
                        onClick={() =>handleOpenHospitalModal()}
                    >
                        <FaSearch />
                    </button>
                )}
            </div>

            <HospitalModal
                show={showHospitalModal}
                handleClose={handleHospitalModalClose}
                onHospitalSelect={handleHospitalSelect}
            />
        </div>
    );
});

export default HospitalSearch;
